/**
 * Lightweight planned-maintenance pub/sub bridge.
 *
 * The Axios response interceptor calls `reportMaintenanceError()` when an API
 * response comes back with HTTP 502 (Bad Gateway) or 503 (Service Unavailable)
 * — the status codes KP infrastructure returns during a planned maintenance
 * window — and calls `reportMaintenanceSuccess()` on the next successful
 * response. UI components subscribe via `subscribeToMaintenance` /
 * `subscribeToMaintenanceRecovered` — no polling, no dependencies beyond
 * Axios itself. Mirrors the `networkStatus.ts` offline-detection pattern.
 *
 * Only genuine state transitions fire notifications:
 *   ok        → maintenance   first 502/503 after a period of success.
 *   maintenance → ok          first successful response after a 502/503.
 *
 * This means repeated 502/503 responses while already in maintenance mode
 * (or repeated successes while already ok) are silently ignored.
 */

/** HTTP status codes treated as planned-maintenance signals. */
export const MAINTENANCE_STATUS_CODES: readonly number[] = [502, 503, 504];

type MaintenanceListener = () => void;

/** Module-level perceived maintenance state — false (ok) until proven otherwise. */
let maintenanceActive = false;

const maintenanceListeners = new Set<MaintenanceListener>();
const recoveredListeners = new Set<MaintenanceListener>();

/**
 * Returns true when the given HTTP status code indicates planned maintenance.
 * @param status - The HTTP status code from an Axios error response, if any.
 */
export const isMaintenanceStatus = (status: number | undefined): boolean =>
  status !== undefined && MAINTENANCE_STATUS_CODES.includes(status);

/**
 * Called by the Axios response interceptor when a response fails with
 * HTTP 502 or 503. Notifies all maintenance subscribers on the first such call.
 */
export const reportMaintenanceError = (): void => {
  if (!maintenanceActive) {
    maintenanceActive = true;
    maintenanceListeners.forEach((fn) => fn());
  }
};

/**
 * Called by the Axios response interceptor on every successful HTTP response.
 * Notifies all recovery subscribers only when recovering from maintenance mode.
 */
export const reportMaintenanceSuccess = (): void => {
  if (maintenanceActive) {
    maintenanceActive = false;
    recoveredListeners.forEach((fn) => fn());
  }
};

/**
 * Subscribe to the maintenance transition (ok → maintenance).
 * Returns an unsubscribe function — call it inside a `useEffect` cleanup.
 *
 * @param fn - Callback invoked on the first 502/503 error.
 * @returns Unsubscribe function.
 */
export const subscribeToMaintenance = (fn: MaintenanceListener): (() => void) => {
  maintenanceListeners.add(fn);
  return (): void => {
    maintenanceListeners.delete(fn);
  };
};

/**
 * Subscribe to the recovery transition (maintenance → ok).
 * Returns an unsubscribe function — call it inside a `useEffect` cleanup.
 *
 * @param fn - Callback invoked on the first successful response after a 502/503.
 * @returns Unsubscribe function.
 */
export const subscribeToMaintenanceRecovered = (fn: MaintenanceListener): (() => void) => {
  recoveredListeners.add(fn);
  return (): void => {
    recoveredListeners.delete(fn);
  };
};

/** Returns the current perceived maintenance state. */
export const getMaintenanceActive = (): boolean => maintenanceActive;

/**
 * Resets module state. **For unit tests only** — do not call in application code.
 * @internal
 */
export const _resetForTests = (): void => {
  maintenanceActive = false;
  maintenanceListeners.clear();
  recoveredListeners.clear();
};
